import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import posthog from 'posthog-js';
import { useAndie } from '@/providers/andie-provider';
import { useAndieChatControls } from '@/providers/andie-chat-provider';
import { ANDIE_ACTION_PRESETS, buildAndiePresetMessage } from '@/ai/data/andie-action-presets';
import { useAndieUsage } from '@/components/andie/chat/AndieUsageMeter';
import { POSTHOG_ENABLED } from '@/providers/posthog-provider';

/**
 * Opens Andie from a link, with a preset already asked.
 *
 *   /main?andie=content-plan
 *
 * Exists so an email or a WhatsApp message can hand someone straight into the thing it talked
 * about, instead of dropping them on a dashboard to find it themselves.
 *
 * Two rules it enforces, both learned the hard way:
 *
 * 1. **Preset keys only, never prompt text.** A url that carried raw text into the model would
 *    let anyone craft a link that makes Andie say whatever they like, inside the product,
 *    looking official. Unknown keys are ignored.
 * 2. **Check the credits before dispatching.** A run started with no credits dies on
 *    ANDIE_USAGE_LIMIT_REACHED, which is a worse outcome than never having sent the link: the
 *    person clicked, trusted us, and got an error. When blocked we open the drawer anyway, so
 *    they land on the credits state and understand what happened.
 *
 * The visible prompt is shown in the chat; the preset's hidden <context-message> block reaches
 * the agent but is stripped from the UI, so the person sees what was asked on their behalf.
 */
const AndieDeepLink = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { t } = useTranslation();
    const { setOpen } = useAndie();
    const { startNewConversation } = useAndieChatControls();
    const { usage, loading } = useAndieUsage();
    const handled = useRef(false);

    const presetKey = searchParams.get('andie');

    useEffect(() => {
        // Wait for the credit state; dispatching before it lands is the failure this guards.
        if (!presetKey || handled.current || loading) return;

        handled.current = true;

        // Always drop the parameter, whatever happens next: a refresh must not fire a second run.
        const next = new URLSearchParams(searchParams);
        next.delete('andie');
        setSearchParams(next, { replace: true });

        const preset = ANDIE_ACTION_PRESETS.find((candidate) => candidate.key === presetKey);
        if (!preset) {
            if (POSTHOG_ENABLED) {
                posthog.capture('andie.deeplink.rejected', { preset: presetKey, reason: 'unknown' });
            }
            return;
        }

        const blocked = !!usage?.blocked;
        setOpen(true);

        if (POSTHOG_ENABLED) {
            posthog.capture('andie.deeplink.opened', {
                preset: preset.key,
                blocked,
                remainingCredits: usage?.remainingCredits ?? null,
            });
        }

        if (blocked) return;

        startNewConversation(buildAndiePresetMessage(preset, t(preset.messageKey)));
    }, [presetKey, loading, usage, searchParams, setSearchParams, setOpen, startNewConversation, t]);

    return null;
};

export default AndieDeepLink;
